import * as Sentry from '@sentry/browser'
import { defineStore } from 'pinia'
import { get } from 'lodash'
import { Service } from '@/services/helpers/ServiceHelper'

const WEB_APP_URL_DOMAIN = import.meta.env.VITE_WEB_APP_URL_DOMAIN

type DevicePrefs = {
  isMobileDevice: boolean | null | undefined,
  platform: string | null | undefined,
  isAppOrWebWithMobile: boolean | null,
  skipMobileInvite: boolean
}

type DownloadAsCsvParams = {
  filename: string,
  data: string,
  successMsg?: string
}

const getDefaultState = () => ({
  isMobileDevice: null,
  platform: null,
  isAppOrWebWithMobile: null,
  skipMobileInvite: localStorage.getItem('skipMobileInvite') === 'true'
} as DevicePrefs)

const blobToBase64 = (blob: Blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  }) as Promise<string | ArrayBuffer | null>
}

const generateAndDownloadFile = (file: { download: string, href: string }) => {
  const link = document.createElement('a')
  link.download = file.download
  link.href = file.href
  link.click()
}

export const useDeviceStore = defineStore('device', () => {
  const router = useRouter()
  const route = useRoute()
  const snackbar = useSnackbar()

  const link = {
    appStore: 'https://apps.apple.com/us/app/emissary-text-recruiting/id1487768297',
    playStore: 'https://play.google.com/store/apps/details?id=com.emissary.android'
  }

  // STATE
  const devicePrefs = ref(getDefaultState())
  const platform = () => computed({
    get: () => devicePrefs.value.platform,
    set: (value) => { devicePrefs.value.platform = value }
  })
  const skipMobileInvite = () => computed({
    get: () => devicePrefs.value.skipMobileInvite,
    set: (value) => {
      localStorage.setItem('skipMobileInvite', value.toString())
      devicePrefs.value.skipMobileInvite = value
    }
  })

  // DETECTION
  const isMobileDevice = async () => {
    if (devicePrefs.value.isMobileDevice === null) {
      try {
        devicePrefs.value.isMobileDevice = !!get(navigator, 'app')
        await new Promise(resolve => setTimeout(resolve, 1000))
        const platformInformation = await Service.capacitor.deviceCapacitor?.getDevicePlatformInfo()
        devicePrefs.value.isMobileDevice = platformInformation?.isMobile
        devicePrefs.value.platform = platformInformation?.platform
      } catch (error) {
        Sentry.captureException(error)
      }
    }
    return devicePrefs.value.isMobileDevice
  }
  const doIfNotMobile = async (callback: Function) => {
    if (await isMobileDevice()) { return }
    callback()
  }
  const isBrowserIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !get(window, 'MSStream')
  }
  const isBrowserAndroid = () => {
    return /Android/i.test(navigator.userAgent)
  }
  const isBrowserMobile = () => {
    return isBrowserIOS() || isBrowserAndroid()
  }
  const isAppOrWebWithMobile = async () => {
    try {
      const isAppOrWebWithMobile = (await isMobileDevice()) || isBrowserMobile()
      devicePrefs.value.isAppOrWebWithMobile = isAppOrWebWithMobile
      return isAppOrWebWithMobile
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not determine device type.')
      return false
    }
  }
  const inviteToDownloadIfMobileBrowser = async () => {
    try {
      const comesFromPlatform = String(route.query.comesFromPlatform).toLowerCase()
      const { isPublicRoute } = usePublicRoutes()
      const isMobileOrLoginFromMobile = await isMobileDevice() || ['ios', 'android'].includes(comesFromPlatform)
      const routeName = route.name
      const isCurrentRoutePublic = typeof routeName === 'string' && isPublicRoute(routeName)
      if (!isMobileOrLoginFromMobile && isBrowserMobile() && !skipMobileInvite().value && !isCurrentRoutePublic) {
        const dialog = useDialog()
        dialog.info({
          text: 'It appears you\'re accessing our services from a mobile device. Did you know there\'s a convenient Emissary mobile app available? Simply click the button below to swiftly download the app and enjoy an enhanced experience.',
          title: 'Check our mobile App',
          action: () => {
            const urlToRedirect = isBrowserIOS() ? link.appStore : link.playStore
            location.href = urlToRedirect
          },
          cancelAction: () => {
            skipMobileInvite().value = true
          },
          dialogProps: { confirmText: 'Download app', cancelText: 'Keep using web version' }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('An error occurred while checking for mobile app invitation.')
    }
  }

  // ROUTING
  const removeDoubleSlashes = (url: string) => {
    // Avoids replacing the '//' in 'https://'
    return url.replace(/([^:]\/)\/+/g, '$1')
  }
  const deepLinkingOnUrlOpen = (event : { url: string }) => {
    Service.capacitor.browserCapacitor?.close()
    const splittedUrl = event.url.split('')
    const mobileAuthUrlDomain = WEB_APP_URL_DOMAIN.replace(/^https?:\/\//, '')
    const indexOfMobileAuthUrl = event.url.indexOf(mobileAuthUrlDomain)
    const slug = splittedUrl
      .slice(indexOfMobileAuthUrl + mobileAuthUrlDomain.length)
      .join('')
      .replace(/^\//, '')
    if (slug) {
      const route = removeDoubleSlashes(`/${slug}`)
      router.push(route)
      return
    }
    router.push({ path: '/' })
  }
  const addDeepLinking = async () => {
    try {
      const { checkAndUpdatePermission, clearAllNotifications } = usePushNotificationStore()
      if (!(await isMobileDevice())) { return }
      Service.capacitor.appCapacitor?.addAppListeners([
        {
          event: 'appUrlOpen',
          handler: (event: any) => {
            deepLinkingOnUrlOpen(event)
          }
        },
        {
          event: 'appStateChange',
          handler: ({ isActive } : { isActive: boolean }) => {
            if (isActive) {
              checkAndUpdatePermission()
              clearAllNotifications()
            }
          }
        }
      ])
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not add deep linking.')
    }
  }
  const openBrowserWith = async ({ url, windowName }: { url: string, windowName: string}) => {
    try {
      const safeUrl = removeDoubleSlashes(url)
      if (await isMobileDevice()) {
        await Service.capacitor.browserCapacitor?.openBrowserWith(safeUrl, windowName)
      } else {
        window.location.replace(safeUrl)
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not open the browser.')
    }
  }
  const closeAppBrowser = async () => {
    try {
      if (!await isMobileDevice()) { return }
      Service.capacitor.browserCapacitor?.close()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not close the browser.')
    }
  }
  const redirectToWebAppForLogin = async ({ email }: { email: string }) => {
    try {
      const platform = devicePrefs.value.platform || 'unknown'
      const encodedEmail = encodeURIComponent(email || '')
      const browserUrl = `${WEB_APP_URL_DOMAIN}/app-login/?comesFromPlatform=${platform}&email=${encodedEmail}`
      await openBrowserWith({ url: browserUrl, windowName: 'Emissary Login' })
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not redirect to web app for login.')
    }
  }
  const redirectToWebAppForLogout = async () => {
    try {
      const platform = devicePrefs.value.platform || 'unknown'
      const browserUrl = `${WEB_APP_URL_DOMAIN}/logout/?comesFromPlatform=${platform}`
      await openBrowserWith({ url: browserUrl, windowName: 'Emissary Logout' })
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not redirect to web app for logout.')
    }
  }

  // ACTIONS
  const copyToClipboard = async (payload: { value: string, text: string } | undefined) => {
    try {
      const value = payload?.value ?? ''
      if (await isMobileDevice()) {
        await Service.capacitor.clipboardCapacitor?.copyToClipboard(value)
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else {
        // Fallback for non-secure contexts / webviews where the async Clipboard API is unavailable
        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      if (payload?.text) {
        snackbar.notify(`${payload.text} copied to clipboard.`)
      } else {
        snackbar.notify('Copied to clipboard.')
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Could not copy to clipboard.')
    }
  }

  const downloadAsCsv = async ({ filename, data, successMsg = '' }: DownloadAsCsvParams) => {
    try {
      if (await isMobileDevice()) {
        const result = await Service.capacitor.saveFilePickerCapacitor?.saveFile({
          filename,
          data,
          mimeType: 'text/csv',
          isBase64: false
        })

        if (!result) { throw new Error('Save file picker is not available') }
        if (result.cancelled) { return }
        const snackbarMessage = successMsg || `The file "${filename}" has been successfully downloaded.`
        snackbar.success(snackbarMessage)
      } else {
        data = 'data:text/csv;charset=utf-8,' + encodeURIComponent(data)
        generateAndDownloadFile({ download: filename, href: data })
        const snackbarMessage = successMsg || `The file "${filename}" has been successfully downloaded.`
        snackbar.success(snackbarMessage)
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('An error occurred when trying to download the CSV file.')
    }
  }

  const downloadAsPdf = async ({ filename, content }: { filename: string, content: Blob | undefined }) => {
    if (!content) { return }
    try {
      const blob = new Blob([content], { type: 'application/pdf' })
      let data = await blobToBase64(blob) as string

      if (await isMobileDevice()) {
        data = data.includes(',') ? data.split(',')[1] : data

        const result = await Service.capacitor.saveFilePickerCapacitor?.saveFile({
          filename,
          data,
          mimeType: 'application/pdf',
          isBase64: true
        })

        if (!result) { throw new Error('Save file picker is not available') }
        if (result.cancelled) { return }
        snackbar.success(`The file "${filename}" has been successfully downloaded.`)
      } else {
        generateAndDownloadFile({ download: filename, href: data })
        snackbar.success(`The file "${filename}" has been successfully downloaded.`)
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('An error occurred when trying to download the PDF file.')
    }
  }

  return {
    link,
    readable: {
      platform,
      devicePrefs
    },

    // DETECTION
    isMobileDevice,
    doIfNotMobile,
    isBrowserIOS,
    isBrowserAndroid,
    isBrowserMobile,
    isAppOrWebWithMobile,
    inviteToDownloadIfMobileBrowser,

    // ROUTING
    addDeepLinking,
    openBrowserWith,
    closeAppBrowser,
    redirectToWebAppForLogin,
    redirectToWebAppForLogout,

    // ACTIONS
    copyToClipboard,
    downloadAsPdf,
    downloadAsCsv
  }
})
